import { Injectable, signal } from '@angular/core';
import { Observable, of, map, catchError, throwError } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import {
  ProductDetail,
  SKUItem,
  DropdownOption,
  TreeNode,
  ProductType,
  StatusUtils,
  ProductStatus,
  SpuTypeUtils,
} from '../models/product.interface';
import { GoodsSkuControllerService } from '../../../api/api/goodsSkuController.service';
import { GoodsSkuPageDTO } from '../../../api/model/goodsSkuPageDTO';
import { PageInfoGoodsSkuPageVO } from '../../../api/model/pageInfoGoodsSkuPageVO';
import { GoodsSkuPageVO } from '../../../api/model/goodsSkuPageVO';
import { CategoryControllerService } from '../../../api/api/categoryController.service';
import { CategoryTreeVO } from '../../../api/model/categoryTreeVO';
import { SkuAuditControllerService } from '../../../api/api/skuAuditController.service';
import { SkuAuditDTO } from '../../../api/model/skuAuditDTO';
import { SkuAuditVO } from '../../../api/model/skuAuditVO';
import { AuditDTO } from '../../../api/model/auditDTO';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  constructor(
    private goodsSkuService: GoodsSkuControllerService,
    private categoryService: CategoryControllerService,
    private skuAuditService: SkuAuditControllerService,
    private http: HttpClient
  ) {}

  readonly categoryOptions: DropdownOption[] = [
    { label: 'Physical Product', value: 'Physical Product' },
    { label: 'Virtual Product', value: 'Virtual Product' },
  ];

  // Updated to use StatusUtils for consistent status options
  readonly statusOptions = StatusUtils.getStatusOptionsForForm();

  /**
   * Get category tree from backend
   */
  getCategoryTree(): Observable<TreeNode[]> {
    // Use the real API call with default parameters
    return this.categoryService
      .getTree(
        undefined, // statusFlag - undefined means all statuses
        undefined, // subFlag - undefined means all categories
        false // removeNotSubFlag - false to include all categories
      )
      .pipe(
        map((categories: CategoryTreeVO[]) => {
          return this.transformCategoryTree(categories);
        }),
        catchError((error) => {
          console.error('Error fetching category tree:', error);
          // Fallback to empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Transform backend CategoryTreeVO to frontend TreeNode format
   */
  private transformCategoryTree(categories: CategoryTreeVO[]): TreeNode[] {
    return categories.map((category) => ({
      label: category.name || '',
      value: (category.id || 0).toString(), // Convert number to string for TreeNode
      children: (category as any).children
        ? this.transformCategoryTree((category as any).children)
        : undefined,
    }));
  }

  /**
   * Safely extract category ID from TreeSelect node without circular references
   */
  private extractCategoryId(category: any): number | null {
    try {
      // If it's already a string/number (ID), use it directly
      if (typeof category === 'string' || typeof category === 'number') {
        const numValue = parseInt(category.toString(), 10);
        return isNaN(numValue) ? null : numValue;
      }

      // If it's an object, extract the value safely
      if (category && typeof category === 'object') {
        // Check for value property first (TreeSelect with optionValue)
        if (category.value !== null && category.value !== undefined) {
          const numValue = parseInt(category.value.toString(), 10);
          return isNaN(numValue) ? null : numValue;
        }

        // Check for id property
        if (category.id !== null && category.id !== undefined) {
          const numValue = parseInt(category.id.toString(), 10);
          return isNaN(numValue) ? null : numValue;
        }

        // Check for key property
        if (category.key !== null && category.key !== undefined) {
          const numValue = parseInt(category.key.toString(), 10);
          return isNaN(numValue) ? null : numValue;
        }

        // Additional check: look for any property that might be a numeric ID
        for (const key of Object.keys(category)) {
          const value = category[key];
          if (value !== null && value !== undefined) {
            // Skip functions and complex objects
            if (
              typeof value === 'function' ||
              (typeof value === 'object' &&
                value !== null &&
                typeof value === 'object')
            ) {
              continue;
            }

            const numValue = parseInt(value.toString(), 10);
            if (!isNaN(numValue) && numValue > 0) {
              console.log(`Found ID in property ${key}:`, numValue);
              return numValue;
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.warn('Error extracting category ID:', error);
      return null;
    }
  }

  /**
   * Get SKU items from backend with pagination and filtering
   */
  getSkuItems(
    page: number = 0,
    size: number = 20,
    searchData: any = {}
  ): Observable<{
    items: SKUItem[];
    total: number;
    page: number;
    size: number;
  }> {
    const request: GoodsSkuPageDTO = {
      page,
      size,
    };

    // Map search parameters to backend fields
    if (searchData.skuName) {
      request.skuName = searchData.skuName;
    }

    if (searchData.productName) {
      request.spuName = searchData.productName;
    }

    if (searchData.category) {
      const spuTypeValue = SpuTypeUtils.getBackendValue(searchData.category);
      request.spuType = spuTypeValue.toString();
    }

    if (searchData.setFlag !== undefined) {
      request.setFlag = searchData.setFlag;
    }

    // Handle categoryIdList from treeselect

    request.categoryIdList = searchData.categoryIdList;

    if (
      searchData.status &&
      Array.isArray(searchData.status) &&
      searchData.status.length > 0
    ) {
      // Convert frontend status labels to backend status values
      const backendStatuses: number[] = [];
      searchData.status.forEach((statusLabel: string) => {
        const frontendStatuses =
          StatusUtils.getStatusValuesByLabel(statusLabel);
        frontendStatuses.forEach((frontendStatus) => {
          const backendStatus =
            StatusUtils.convertToBackendStatus(frontendStatus);
          if (!backendStatuses.includes(backendStatus)) {
            backendStatuses.push(backendStatus);
          }
        });
      });
      request.status = backendStatuses.map((s) => s.toString());
    } else {
      // When no statuses are selected, show every status Product Review handles — excludes
      // Draft(0), which is never submitted for review.
      const allBackendStatuses = [1, 2, 3, 4, 5];
      request.status = allBackendStatuses.map((s) => s.toString());
    }

    return this.goodsSkuService
      .getList3(
        request.spuName,
        request.spuCode,
        request.spuType ? parseInt(request.spuType) : undefined,
        request.setFlag,
        request.categoryIdList,
        request.status ? request.status.map((s) => parseInt(s)) : undefined,
        request.skuName,
        request.skuCode,
        request.page,
        request.size
      )
      .pipe(
        map((response: PageInfoGoodsSkuPageVO) => {
          const items = this.transformBackendData(response.pageContent || []);
          return {
            items,
            total: response.totalElements || 0,
            page: response.number || 0,
            size: response.size || 20,
          };
        })
      );
  }

  /**
   * Transform backend GoodsSkuPageVO to frontend SKUItem
   */
  private transformBackendData(backendItems: GoodsSkuPageVO[]): SKUItem[] {
    return backendItems.map((item) => ({
      id: item.id,
      spuId: item.spuId,
      spuName: item.spuName,
      spuCode: item.spuCode,
      spuType:
        item.spuType !== undefined && item.spuType !== null
          ? SpuTypeUtils.getFrontendLabel(item.spuType)
          : '',
      categoryCode: item.categoryCode,
      setFlag: item.setFlag,
      materialId: item.materialId,
      materialName: item.materialName,
      materialMainImages: item.materialMainImages,
      // Add first image URL for table display
      materialMainImage:
        item.materialMainImages && item.materialMainImages.length > 0
          ? item.materialMainImages[0]
          : '',
      skuCode: item.skuCode,
      skuName: item.skuName,
      categoryName: item.categoryName,
      showFlag: item.showFlag,
      goodsRemark: item.goodsRemark,
      status:
        item.status !== undefined
          ? StatusUtils.convertToFrontendStatus(item.status as any)
          : undefined,
      costPrice: item.costPrice,
      salesPrice: item.salesPrice,
      activityPrice: item.activityPrice,
      marketPrice: item.marketPrice,
      distributionPrice: item.distributionPrice,
      wholesalePrice: item.wholesalePrice,
      clearingPrice: item.clearingPrice,
      weight: item.weight,
      length: item.length,
      width: item.width,
      height: item.height,
      auditIdea: item.auditIdea,
      auditCommitDate: item.auditCommitDate,
      skuAttributeList: item.skuAttributeList,
      otherAttributeList: item.otherAttributeList,
    }));
  }

  /**
   * Get product detail from backend using SKU audit detail API
   */
  getProductDetail(id: number, type: ProductType): Observable<ProductDetail> {
    const request: SkuAuditDTO = { id };

    return this.skuAuditService.detail(request).pipe(
      map((response: SkuAuditVO) => {
        // Use draftSkuVO if available, otherwise fall back to thisSkuVO
        const skuData = response.draftSkuVO || response.thisSkuVO;

        if (!skuData) {
          throw new Error('No SKU data available');
        }

        return this.transformSkuAuditToProductDetail(skuData, type);
      }),
      catchError((error) => {
        console.error('Error fetching product detail:', error);
        return throwError(() => new Error('Failed to load product details'));
      })
    );
  }

  /**
   * Transform backend SkuAuditVO to frontend ProductDetail
   */
  private transformSkuAuditToProductDetail(
    skuData: any,
    type: ProductType
  ): ProductDetail {
    return {
      // Basic identification
      id: skuData.id,
      spuId: skuData.spuId,

      // SKU information
      skuName: skuData.skuName,
      skuCode: skuData.skuCode,

      // SPU information
      productName: skuData.spuName,
      productCode: skuData.spuCode,

      // Category and brand
      productCategories: skuData.categoryName,
      productBrand: skuData.brandName,

      // Material information
      materialName: skuData.materialName,

      // Physical dimensions
      productWeight: skuData.weight,
      productLength: skuData.length,
      productWidth: skuData.width,
      productHeight: skuData.height,

      // Pricing information (from priceVO)
      costPrice: skuData.priceVO?.costPrice,
      salesPrice: skuData.priceVO?.salesPrice,
      marketingPrice: skuData.priceVO?.activityPrice,
      marketPrice: skuData.priceVO?.marketPrice,
      distributionPrice: skuData.priceVO?.distributionPrice,
      wholesalePrice: skuData.priceVO?.wholesalePrice,
      settlementPrice: skuData.priceVO?.clearingPrice,
      internalApportionmentPrice: 0, // Not available in backend model

      // Images and content
      mainImage: skuData.mainImage,
      detailedImage: skuData.detailImage,
      productDescription: skuData.detailHtml,

      // Display and status
      showFlag: skuData.showFlag,
      status: skuData.status,

      // Audit information
      auditComment: skuData.auditIdea,
      submittedBy: skuData.auditCommitName,
      auditCommitDate: skuData.auditCommitDate,
      auditCommitBy: skuData.auditCommitBy,

      // Additional fields from backend
      md5CheckValue: skuData.md5CheckValue,

      // Attribute lists
      skuAttributeList: skuData.skuAttributeList,
      otherAttributeList: skuData.otherAttributeList,

      // Package-specific information
      skuDetailList: skuData.skuDetailList,
    };
  }

  /**
   * Submit audit using backend API
   */
  submitAudit(
    productId: number,
    action: 'pass' | 'turnDown',
    comment: string
  ): Observable<boolean> {
    const request: AuditDTO = {
      id: productId,
      flag: action === 'pass',
      auditIdea: comment,
    };

    return this.skuAuditService.audit(request).pipe(
      catchError((error) => {
        console.error('Error submitting audit:', error);
        return throwError(() => new Error('Failed to submit audit'));
      })
    );
  }
}
